import React, { useEffect, useRef, useState } from "react";
import { Alert, Button } from "antd";

import AvatarQueiroz from "../../assets/image/avatar-queiroz.png";
import { getTokenInfo } from "../../services/auth";
import odinApi from "../../services/odinApi";

import MessageRenderer from "./MessageRenderer";

import {
  Drawer,
  WelcomeContent,
  AvatarImage,
  Title,
  Description,
  StartButton,
  ChatContainer,
  ChatHeader,
  LeftHeader,
  CloseIcon,
  HeaderAvatar,
  HeaderName,
  TrashIcon,
  ChatFooter,
  ChatInput,
  MessagesWrapper,
  MessageBlock,
  MessageAvatar,
  MessageContent,
  MessageName,
  WrapperChat,
  MessageBody,
  TypingIndicator,
  ButtonTrash,
} from "./styles";

interface IDrawerChatbotProps {
  visibleChatbot: boolean;
  setVisibleChatbot: React.Dispatch<React.SetStateAction<boolean>>;
}

interface Message {
  sender: "user" | "bot";
  content: string;
}

const DrawerChatbot: React.FC<IDrawerChatbotProps> = ({
  visibleChatbot,
  setVisibleChatbot,
}) => {
  const [started, setStarted] = useState(true);
  const [inputValue, setInputValue] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const savedMessages = localStorage.getItem("chatbotMessages");
    const savedThreadId = localStorage.getItem("chatbotThreadId");

    if (savedMessages) {
      setMessages(JSON.parse(savedMessages));
      setStarted(true);
    }

    if (savedThreadId) {
      setThreadId(savedThreadId);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("chatbotMessages", JSON.stringify(messages));
    if (threadId) {
      localStorage.setItem("chatbotThreadId", threadId);
    }
  }, [messages, threadId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (text: string, isBotMessage = false) => {
    setInputValue("");
    if (!text.trim()) return;

    if (!isBotMessage) {
      setMessages((prev) => [...prev, { sender: "user", content: text }]);
    }

    setIsTyping(true);

    try {
      const { data } = await odinApi.post(
        "/gpt/message",
        {
          message: text,
          thread_id: threadId,
        },
        {
          headers: { Source: "WEB" },
        }
      );

      const newThreadId = data?.data?.thread_id;
      setThreadId(newThreadId);

      const content = data?.data?.data?.[0]?.content?.[0]?.text?.value;
      if (content) {
        setMessages((prev) => [...prev, { sender: "bot", content }]);
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          content:
            "Serviço temporariamente indisponível. Tente novamente mais tarde.",
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleRestart = () => {
    setStarted(true);
    localStorage.removeItem("chatbotMessages");
    localStorage.removeItem("chatbotThreadId");
    setMessages([]);
    setThreadId(null);
  };

  return (
    <Drawer
      visible={visibleChatbot}
      width={window.outerWidth < 768 ? "100%" : "26%"}
      bodyStyle={{ padding: 0 }}
      closable={false}
      title={null}
      closeIcon={false}
      footer={
        !started ? (
          <Alert
            message="Esta é uma ferramenta de Inteligência Artificial. As respostas podem conter erros ou informações desatualizadas. Em caso de dúvidas críticas, contate o time de Suporte da Franqueadora."
            type="info"
            showIcon
          />
        ) : null
      }
    >
      {started && messages.length === 0 ? (
        <>
          <ChatHeader>
            <LeftHeader>
              <CloseIcon onClick={() => setVisibleChatbot(false)} />
            </LeftHeader>
          </ChatHeader>
          <WelcomeContent>
            <AvatarImage src={AvatarQueiroz} alt="Queiroz Avatar" />
            <Title>Olá! Eu sou o Queiroz Bot</Title>
            <Description>
              Seu assistente virtual da The Best Açaí! Estou aqui para te ajudar
              com dúvidas sobre o sistema, processos e tudo relacionado à sua
              franquia.
            </Description>
            <StartButton onClick={() => setStarted(false)}>
              Começar conversa
            </StartButton>
          </WelcomeContent>
        </>
      ) : (
        <WrapperChat>
          <ChatHeader>
            <LeftHeader>
              <CloseIcon onClick={() => setVisibleChatbot(false)} />
              <HeaderAvatar src={AvatarQueiroz} />
              <HeaderName>Queiroz Bot</HeaderName>
            </LeftHeader>

            <ButtonTrash onClick={handleRestart}>
              <TrashIcon />
            </ButtonTrash>
          </ChatHeader>

          <ChatContainer>
            <MessagesWrapper>
              <MessageBlock key={"default"}>
                <MessageAvatar src={AvatarQueiroz} />

                <MessageBody>
                  <MessageName>Queiroz Bot</MessageName>
                  <MessageContent isUser={false}>
                    Olá! Eu sou o Queiroz{" "}
                    <span role="img" aria-label="robô">
                      🤖
                    </span>
                    , assistente da The Best Açaí. Posso te ajudar com dúvidas
                    sobre o sistema, pedidos, cupons, ocorrências e outros
                    processos da loja. O que você precisa saber?
                  </MessageContent>
                </MessageBody>
              </MessageBlock>
              {messages.map((message, index) => (
                <MessageBlock key={index}>
                  {message.sender === "bot" && (
                    <MessageAvatar src={AvatarQueiroz} />
                  )}
                  {message.sender === "user" && (
                    <MessageAvatar
                      src={
                        getTokenInfo()?.image
                          ? getTokenInfo()?.image
                          : "https://www.ecp.org.br/wp-content/uploads/2017/12/default-avatar-1.png"
                      }
                    />
                  )}
                  <MessageBody>
                    <MessageName>
                      {message.sender === "user" ? "Você" : "Queiroz Bot"}
                    </MessageName>
                    <MessageContent isUser={message.sender === "user"}>
                      <MessageRenderer content={message.content} />
                    </MessageContent>
                  </MessageBody>
                </MessageBlock>
              ))}
              <div ref={messagesEndRef} />
              {isTyping && (
                <TypingIndicator>Queiroz Bot está digitando...</TypingIndicator>
              )}
            </MessagesWrapper>
          </ChatContainer>

          <ChatFooter>
            <ChatInput
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              onPressEnter={() => sendMessage(inputValue)}
              placeholder="Tire suas dúvidas sobre o sistema e os processos da loja"
              disabled={isTyping}
            />
            <Button
              type="primary"
              onClick={() => sendMessage(inputValue)}
              disabled={!inputValue.trim()}
              style={{ height: "45px" }}
            >
              Enviar
            </Button>
          </ChatFooter>
        </WrapperChat>
      )}
    </Drawer>
  );
};

export default DrawerChatbot;
